<script>
import mealType from '@/store/type/mealType'
import FormInput from '@/components/mixins/FormInput'
import FormTabMixin from '@/components/mixins/FormTabMixin'

import SelectApiClientType from '@/api/SelectApiClientType'
import MapperCollection from '@/services/mapper/MapperCollection'
import MapperType from '@/services/mapper/MapperType'
import RestApiCollection from '@/api/RestApiCollection'
import RestApiType from '@/api/RestApiType'
import selectapiType from '@/store/type/selectapiType'

import TextInput from '@/components/form/Inputs/TextInput'
import MarkdownInput from '@/components/form/Inputs/MarkdownInput'

import EntitySelectInput from '@/components/form/Inputs/EntitySelectInput'

import FormTwoColRow from '@/components/form/FormTwoColRow'
import FormPanel from '@/components/form/FormPanel'
import ConfirmModal from '@/components/confirm/ConfirmModal'
import CheckboxInput from '@/components/form/Inputs/CheckboxInput'
import { mapGetters } from 'vuex'
import authType from '@/store/type/authType'
import ImageEditor from '@/components/pages/meals/ImageEditor'

export default {
    components: {
        ImageEditor,
        MarkdownInput,
        TextInput,
        EntitySelectInput,
        FormTwoColRow,
        FormPanel,
        ConfirmModal,
        CheckboxInput,
    },

    mixins: [FormInput, FormTabMixin],

    data() {
        return {
            selectApiClientType: SelectApiClientType,
            api: RestApiType.MEAL,
            dialog: false,
            mapper: MapperType.MEAL_FORM,
            suitableForConstraints: {},
        }
    },

    computed: {
        ...mapGetters({
            isRestaurantAdmin: authType.getters.IS_RESTAURANT_ADMIN,
        }),
        invalidAllergens() {
            let invalidAllergens = []
            if (
                this.suitableForConstraints?.invalid_allergens &&
                this.model.suitableFor
            ) {
                this.model.suitableFor.map((el) => {
                    let key = Object.keys(
                        this.suitableForConstraints.invalid_allergens
                    ).find((key) => {
                        return key == el
                    })
                    if (key)
                        invalidAllergens = [
                            ...invalidAllergens,
                            ...this.suitableForConstraints.invalid_allergens[
                                key
                            ],
                        ]
                })
            }
            return invalidAllergens
        },

        invalidAdditionals() {
            let invalidAdditionals = []
            if (
                this.suitableForConstraints?.invalid_additionals &&
                this.model.suitableFor
            ) {
                this.model.suitableFor.map((el) => {
                    let key = Object.keys(
                        this.suitableForConstraints.invalid_additionals
                    ).find((key) => {
                        return key == el
                    })
                    if (key)
                        invalidAdditionals = [
                            ...invalidAdditionals,
                            ...this.suitableForConstraints.invalid_additionals[
                                key
                            ],
                        ]
                })
            }
            return invalidAdditionals
        },
    },

    watch: {
        'model.suitableFor': {
            handler(suitableFor) {
                if (suitableFor?.length) {
                    if (this.invalidAllergens?.length) {
                        this.$set(
                            this.model,
                            'allergens',
                            this.model.allergens.filter((el) => {
                                return this.invalidAllergens.indexOf(el) === -1
                            })
                        )
                    }
                    if (this.invalidAdditionals?.length) {
                        this.$set(
                            this.model,
                            'additional',
                            this.model.additional.filter((el) => {
                                return (
                                    this.invalidAdditionals.indexOf(el) === -1
                                )
                            })
                        )
                    }
                }
            },
            immediate: true,
        },
    },

    mounted() {
        this.$store
            .dispatch(
                selectapiType.getActionName(
                    this.selectApiClientType.SUITABLE_FOR_CONSTRAINTS,
                    selectapiType.actions.LOAD
                )
            )
            .then((res) => {
                this.suitableForConstraints = res
            })
    },

    methods: {
        back() {
            this.$router.push(
                {
                    query: { ...this.$route.query, meal: undefined },
                },
                () => {
                    this.$store.dispatch(mealType.actions.SET, {})
                }
            )
        },

        setThumbnail() {
            this.model.thumbMedia = this.model.imageMedia
        },

        deleteMeal() {
            RestApiCollection.get(this.api)
                .delete(this.model.id)
                .then(() => {
                    this.back()
                })
        },

        save() {
            const entity = MapperCollection.get(this.mapper).toEntity(
                this.model
            )

            if (this.model.id) {
                RestApiCollection.get(this.api)
                    .update(entity, this.model.id)
                    .then(() => {
                        this.success()
                    })
            } else {
                RestApiCollection.get(this.api)
                    .create(entity)
                    .then(() => {
                        this.success()
                    })
            }
        },

        success() {
            this.$store.dispatch(mealType.actions.SET, {})
            this.$router.push({
                query: { ...this.$route.query, meal: undefined },
            })
        },
    },
}
</script>

<template>
    <div class="meal-page">
        <div class="my-4">
            <v-btn color="primary" small class="ml-2" @click="back">
                {{ $t('actions.back') }}
            </v-btn>
            <form @submit.prevent="save">
                <form-panel :title="$t('labels.general_settings')">
                    <form-two-col-row>
                        <template v-slot:col1>
                            <text-input
                                v-model="model.name"
                                v-tab-error:menu
                                v-validate.immediate="'required|space'"
                                :required="true"
                                name="name"
                                label="labels.name"
                                :error-messages="errors.collect('name')"
                                :data-vv-as="$t('labels.name')"
                            ></text-input>
                        </template>
                        <template v-slot:col2>
                            <text-input
                                v-model="model.price"
                                v-tab-error:menu
                                v-validate.immediate="'required|decimal'"
                                name="price"
                                label="labels.price"
                                :error-messages="errors.collect('price')"
                                :data-vv-as="$t('labels.price')"
                            ></text-input>
                        </template>
                    </form-two-col-row>
                    <markdown-input
                        v-model="model.description"
                        v-validate="'space'"
                        v-tab-error:menu
                        name="description"
                        label="labels.description"
                        class="mt-4"
                        :error-messages="errors.collect('description')"
                        :data-vv-as="$t('labels.description')"
                    ></markdown-input>
                    <text-input
                        v-if="!isRestaurantAdmin"
                        v-model="model.code"
                        v-tab-error:menu
                        v-validate="'space'"
                        name="code"
                        label="labels.code"
                        :error-messages="errors.collect('code')"
                        :data-vv-as="$t('labels.code')"
                    ></text-input>
                    <text-input
                        v-if="!isRestaurantAdmin"
                        v-model.number="model.deliveryUnit"
                        v-tab-error:menu
                        v-validate.immediate="'required|numeric|min_value:0'"
                        name="deliveryUnit"
                        label="labels.delivery_unit"
                        :error-messages="errors.collect('deliveryUnit')"
                        :data-vv-as="$t('labels.delivery_unit')"
                    ></text-input>
                    <entity-select-input
                        v-model="model.category"
                        v-tab-error:menu
                        v-validate.immediate="'required'"
                        label="labels.meal_category"
                        name="mealCategory"
                        :query-params="{ restaurant: model.restaurant }"
                        :entity="selectApiClientType.MEAL_CATEGORY"
                        :data-vv-as="$t('labels.meal_category')"
                        :error-messages="errors.collect('mealCategory')"
                    ></entity-select-input>
                    <checkbox-input
                        v-model="model.isActive"
                        label="labels.active"
                        name="isActive"
                    ></checkbox-input>
                </form-panel>

                <form-panel
                    v-if="!isRestaurantAdmin"
                    :title="$t('labels.images')"
                >
                    <image-editor
                        v-model="model"
                        :primary-aspect-ratio="1.75438596491"
                        :secondary-aspect-ratio="1"
                        :primary-size="{ height: 284, width: 499 }"
                        :secondary-size="{ height: 160, width: 160 }"
                        primary-key="imageMedia"
                        secondary-key="thumbMedia"
                        primary-label="labels.image_media"
                        secondary-label="labels.thumb_media"
                    ></image-editor>
                </form-panel>

                <form-panel :title="$t('labels.vat')">
                    <entity-select-input
                        v-model="model.valueAddedTax"
                        v-tab-error:menu
                        v-validate.immediate="'required'"
                        label="labels.vat"
                        name="valueAddedTax"
                        :entity="selectApiClientType.VAT"
                        :data-vv-as="$t('labels.vat')"
                        :error-messages="errors.collect('valueAddedTax')"
                    ></entity-select-input>
                </form-panel>

                <form-panel
                    v-if="model && model.restaurant"
                    :title="$t('labels.meal_option_groups')"
                >
                    <entity-select-input
                        v-model="model.groups"
                        v-tab-error:menu
                        v-validate="''"
                        label="labels.meal_option_groups"
                        name="meal_option_groups"
                        :entity="selectApiClientType.MEAL_OPTION_GROUP"
                        :data-vv-as="$t('labels.meal_option_groups')"
                        :error-messages="errors.collect('meal_option_groups')"
                        :query-params="{ restaurant: model.restaurant }"
                        multi-select
                    ></entity-select-input>
                </form-panel>

                <form-panel :title="$t('labels.allergens')">
                    <entity-select-input
                        v-model="model.suitableFor"
                        v-tab-error:menu
                        v-validate="''"
                        type="checkbox"
                        class="mb-5"
                        label="labels.allergens_suitability"
                        name="allergens_suitability"
                        :entity="selectApiClientType.ALLERGENS_SUITABILITY"
                        :data-vv-as="$t('labels.allergens_suitability')"
                        :error-messages="
                            errors.collect('allergens_suitability')
                        "
                        multi-select
                        translate-items
                    ></entity-select-input>
                    <entity-select-input
                        v-model="model.allergens"
                        v-tab-error:menu
                        v-validate="''"
                        :invalid="invalidAllergens"
                        type="checkbox"
                        class="mb-5"
                        label="labels.allergens"
                        name="allergens"
                        :entity="selectApiClientType.ALLERGENS"
                        :data-vv-as="$t('labels.allergens')"
                        :error-messages="errors.collect('allergens')"
                        multi-select
                        translate-items
                    ></entity-select-input>
                    <entity-select-input
                        v-model="model.additional"
                        v-tab-error:menu
                        v-validate="''"
                        :invalid="invalidAdditionals"
                        type="checkbox"
                        class="mb-5"
                        label="labels.allergens_additional"
                        name="allergens_additional"
                        :entity="selectApiClientType.ALLERGENS_ADDITIONAL"
                        :data-vv-as="$t('labels.allergens_additional')"
                        :error-messages="errors.collect('allergens_additional')"
                        multi-select
                        translate-items
                    ></entity-select-input>
                </form-panel>

                <confirm-modal
                    v-model="dialog"
                    :title="$t('texts.are_you_sure')"
                    @onApprove="deleteMeal"
                ></confirm-modal>

                <portal to="footer">
                    <v-btn
                        v-if="!isRestaurantAdmin"
                        class="mr-4"
                        color="warning"
                        @click="dialog = true"
                    >
                        {{ $t('actions.delete') }}
                    </v-btn>
                    <v-btn color="success" @click="save">
                        {{ $t('actions.save') }}
                    </v-btn>
                </portal>
            </form>
        </div>
    </div>
</template>
